import React, { useEffect, useState } from 'react'
import {Bell, CheckCircle, AlertCircle, DollarSign, Clock, TrendingUp} from 'lucide-react'
import { lumi } from '@/lib/lumi'

interface Activity {
  id: string
  type: 'task_created' | 'task_claimed' | 'task_completed' | 'payment_released' | 'review_submitted'
  title: string
  description: string
  timestamp: string
  icon: 'check' | 'alert' | 'dollar' | 'clock' | 'trend'
  color: string
}

interface ActivityFeedProps {
  userId?: string
}

export function ActivityFeed({ userId }: ActivityFeedProps) {
  const [activities, setActivities] = useState<Activity[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchActivities()
  }, [userId])

  const fetchActivities = async () => {
    setLoading(true)
    try {
      const activities: Activity[] = []

      // Fetch recent tasks
      const { list: tasks } = await lumi.entities.tasks.list({
        filter: {},
        sort: { createdAt: -1 },
        limit: 10
      })

      tasks.forEach((task: any) => {
        if (task.status === 'created') {
          activities.push({
            id: task._id,
            type: 'task_created',
            title: 'New Task Posted',
            description: task.title,
            timestamp: task.createdAt,
            icon: 'clock',
            color: 'blue'
          })
        } else if (task.status === 'claimed') {
          activities.push({
            id: task._id,
            type: 'task_claimed',
            title: 'Task Claimed',
            description: task.title,
            timestamp: task.updatedAt,
            icon: 'trend',
            color: 'purple'
          })
        } else if (task.status === 'completed') {
          activities.push({
            id: task._id,
            type: 'task_completed',
            title: 'Task Completed',
            description: task.title,
            timestamp: task.updatedAt,
            icon: 'check',
            color: 'emerald'
          })
        }
      })

      // Fetch recent transactions
      const { list: transactions } = await lumi.entities.transactions.list({
        filter: {},
        sort: { createdAt: -1 },
        limit: 5
      })

      transactions.forEach((tx: any) => {
        if (tx.status === 'released') {
          activities.push({
            id: tx._id,
            type: 'payment_released',
            title: 'Payment Released',
            description: `$${tx.amount} paid for completed task`,
            timestamp: tx.updatedAt,
            icon: 'dollar',
            color: 'cyan'
          })
        }
      })

      // Sort by timestamp
      activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      setActivities(activities.slice(0, 15))
    } catch (error) {
      console.error('Failed to fetch activities:', error)
    } finally {
      setLoading(false)
    }
  }

  const getIcon = (iconType: string) => {
    switch (iconType) {
      case 'check': return CheckCircle
      case 'alert': return AlertCircle
      case 'dollar': return DollarSign
      case 'clock': return Clock
      case 'trend': return TrendingUp
      default: return Bell
    }
  }

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; text: string; border: string }> = {
      blue: { bg: 'bg-electric-purple-500/20', text: 'text-electric-purple-400', border: 'border-electric-purple-500/50' },
      emerald: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/50' },
      cyan: { bg: 'bg-electric-purple-500/20', text: 'text-electric-purple-400', border: 'border-electric-purple-500/50' },
      purple: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/50' },
      yellow: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/50' }
    }
    return colors[color] || colors.blue
  }

  const formatTimestamp = (timestamp: string) => {
    const now = new Date()
    const time = new Date(timestamp)
    const diffMs = now.getTime() - time.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return time.toLocaleDateString()
  }

  if (loading) {
    return (
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex gap-3">
              <div className="w-10 h-10 bg-slate-800 rounded-lg" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-slate-800 rounded w-3/4" />
                <div className="h-3 bg-slate-800 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-6">
        <Bell className="w-5 h-5 text-electric-purple-400" />
        <h2 className="text-xl font-bold text-white">Recent Activity</h2>
      </div>

      <div className="space-y-4">
        {activities.map((activity) => {
          const Icon = getIcon(activity.icon)
          const colors = getColorClasses(activity.color)
          
          return (
            <div
              key={activity.id}
              className="flex gap-3 items-start p-3 rounded-lg hover:bg-slate-800/50 transition-colors"
            >
              <div className={`w-10 h-10 ${colors.bg} rounded-lg flex items-center justify-center border ${colors.border}`}>
                <Icon className={`w-5 h-5 ${colors.text}`} />
              </div>
              <div className="flex-1">
                <p className="text-white font-semibold">{activity.title}</p>
                <p className="text-slate-400 text-sm mt-0.5">{activity.description}</p>
                <p className="text-slate-500 text-xs mt-1">{formatTimestamp(activity.timestamp)}</p>
              </div>
            </div>
          )
        })}

        {activities.length === 0 && (
          <div className="text-center py-8 text-slate-400">
            <Bell className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No recent activity</p>
          </div>
        )}
      </div>
    </div>
  )
}